import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useIsAdminViewMode } from "../../../hooks/useViewMode";

import PageHeader from "../../../components/PageHeader";
import SettingsCreateWizardLayout from "../SettingsCreateWizardLayout";
import SettingsWizardFooter from "../SettingsWizardFooter";
import { handleEditFormSubmit } from "../../../components/modals/EditableTable";
import ZoneSettingsContenantGrid from "./ZoneSettingsContenantGrid";
import ZoneSettingsMaterials from "./ZoneSettingsMaterials";
import ZoneSettingsManagers from "./ZoneSettingsManagers";
import {
  DEFAULT_ZONE_CREATE_FORM,
  getWizardStepIndex,
  normalizeZoneFormForDirtyCheck,
  prepareZoneUpdatePayload,
  validateAllZoneWizardSteps,
  validateZoneWizardStep,
} from "./zoneSettingsWizard";
import {
  clearZoneCreateDraft,
  getInitialZoneCreateState,
  writeZoneCreateDraft,
} from "./zoneCreateDraft";

const ZoneSettingsCreateEditor = ({ token }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const API = process.env.REACT_APP_API_BACKEND;

  const initialWizardState = useMemo(() => getInitialZoneCreateState(), []);

  const [formData, setFormData] = useState(initialWizardState.formData);
  const [activeStep, setActiveStep] = useState(initialWizardState.activeStep);
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(
    initialWizardState.maxReachedStepIndex,
  );
  const [errorForm, setErrorForm] = useState("");
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [wasteCategories, setWasteCategories] = useState([]);

  const adminUserConnected = useIsAdminViewMode();
  const skipNextDraftPersistRef = useRef(false);

  const steps = useMemo(
    () => [
      { key: "contenant", label: t("Container") },
      {
        key: "materials",
        label: t("containerSettings.unauthorizedMaterials"),
      },
      { key: "managers", label: t("containerSettings.managers") },
    ],
    [t],
  );

  const currentStepIndex = getWizardStepIndex(activeStep);
  const isLastStep = currentStepIndex === steps.length - 1;

  const hasUnsavedChanges = useMemo(() => {
    const initial = JSON.stringify(
      normalizeZoneFormForDirtyCheck(DEFAULT_ZONE_CREATE_FORM),
    );
    const current = JSON.stringify(normalizeZoneFormForDirtyCheck(formData));
    return current !== initial;
  }, [formData]);

  useEffect(() => {
    if (skipNextDraftPersistRef.current) {
      skipNextDraftPersistRef.current = false;
      return;
    }
    writeZoneCreateDraft({ formData, activeStep, maxReachedStepIndex });
  }, [formData, activeStep, maxReachedStepIndex]);

  useEffect(() => {
    if (!token) return;
    const fetchWasteCategories = async () => {
      try {
        const response = await axios.get(
          `${API}wastecategory?page=1&limit=500`,
          {
            headers: { authorization: `Bearer ${token}` },
            params: {
              keywordFilter: "",
              listUnCodeFullFilter: {},
              listUnCodeEmptyFilter: {},
              listWasteCodeFullFilter: {},
              listWasteCodeEmptyFilter: {},
              sortByField: "label",
              orderSortByField: "1",
            },
          },
        );
        if (response?.status === 200) {
          setWasteCategories(response.data?.data || []);
        }
      } catch {
        setWasteCategories([]);
      }
    };
    fetchWasteCategories();
  }, [API, token]);

  useEffect(() => {
    const flowId =
      formData.contentCategory == null || formData.contentCategory === ""
        ? ""
        : String(formData.contentCategory);

    if (!wasteCategories.length) return;

    if (!flowId) {
      const hasAuthorized =
        (formData.contentCategoryJoin?.authorizedMaterial || []).length > 0;
      if (!hasAuthorized) return;
      setFormData((prev) => ({
        ...prev,
        contentCategoryJoin: {
          ...(prev.contentCategoryJoin || {}),
          _id: "",
          name: "",
          label: "",
          authorizedMaterial: [],
        },
      }));
      return;
    }

    const selected = wasteCategories.find((wc) => String(wc._id) === flowId);
    if (!selected) return;

    const joinId = formData.contentCategoryJoin?._id
      ? String(formData.contentCategoryJoin._id)
      : "";
    const nextAuthorized = selected.authorizedMaterial || [];
    const currentAuthorized =
      formData.contentCategoryJoin?.authorizedMaterial || [];
    const sameJoin =
      joinId === flowId &&
      JSON.stringify(currentAuthorized) === JSON.stringify(nextAuthorized);

    if (sameJoin) return;

    setFormData((prev) => ({
      ...prev,
      contentCategoryJoin: {
        ...(prev.contentCategoryJoin || {}),
        _id: selected._id,
        name: selected.label,
        label: selected.label,
        authorizedMaterial: nextAuthorized,
      },
    }));
  }, [formData.contentCategory, formData.contentCategoryJoin, wasteCategories]);

  useEffect(() => {
    if (!token) return;
    const fetchData = async () => {
      try {
        const [responseSite, responseContract] = await axios.all([
          axios.get(`${API}address/getAddressNameList`, {
            headers: { authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}contract/getContractList`, {
            headers: { authorization: `Bearer ${token}` },
          }),
        ]);
        if (responseSite?.status === 200) {
          setSites(responseSite.data?.addressNameList || []);
        }
        if (responseContract?.status === 200) {
          setContracts(responseContract.data?.contractsList || []);
        }
      } catch {
        setSites([]);
        setContracts([]);
      }
    };
    fetchData();
  }, [API, token]);

  const goToStep = (stepKey) => {
    setActiveStep(stepKey);
  };

  const goToContenantGestion = () => {
    goToStep("contenant");
    requestAnimationFrame(() => {
      document
        .getElementById("zone-settings-gestion")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const goToCreateStep = (_index, step) => {
    goToStep(step.key);
  };

  const showValidationError = (message) => {
    toast.error(message, { className: "custom-toast" });
  };

  const goToNextStep = () => {
    const result = validateZoneWizardStep(activeStep, formData, t);
    if (!result.valid) {
      showValidationError(result.message);
      return;
    }
    const idx = currentStepIndex;
    if (idx < steps.length - 1) {
      const nextKey = steps[idx + 1].key;
      goToStep(nextKey);
      setMaxReachedStepIndex((prev) => Math.max(prev, idx + 1));
    }
  };

  const createZone = async () => {
    const validation = validateAllZoneWizardSteps(formData, t);
    if (!validation.valid) {
      showValidationError(validation.message);
      return null;
    }

    setSaving(true);
    try {
      const addResponse = await axios.post(
        `${API}container/add`,
        { address: formData.addressId },
        { headers: { authorization: `Bearer ${token}` } },
      );

      const created = addResponse?.data?.newContainerMongoDB;
      if (!created?._id) {
        toast.error(t("settingsContainers.createError"), {
          className: "custom-toast",
        });
        return null;
      }

      const updatePayload = prepareZoneUpdatePayload({
        ...formData,
        _id: created._id,
      });

      const updateResponse = await handleEditFormSubmit(
        updatePayload,
        "container",
      );

      if (updateResponse?.status === 200) {
        clearZoneCreateDraft();
        toast.success(t("settingsContainers.zoneCreatedSuccess"), {
          className: "custom-toast",
        });
        return created._id;
      }

      toast.error(t("settingsContainers.createError"), {
        className: "custom-toast",
      });
      return null;
    } catch {
      toast.error(t("settingsContainers.createError"), {
        className: "custom-toast",
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const stepContent = (() => {
    switch (activeStep) {
      case "contenant":
        return (
          <div className="zone-settings__contenant-form zone-settings__contenant-form--wizard flex flex-col">
            <ZoneSettingsContenantGrid
              formData={formData}
              setFormData={setFormData}
              setErrorForm={setErrorForm}
              sites={sites}
              contracts={contracts}
              showAdministrator={adminUserConnected}
            />
          </div>
        );
      case "materials":
        return (
          <div className="zone-settings__tab-panel">
            <ZoneSettingsMaterials
              container={formData}
              onModifyFlow={goToContenantGestion}
            />
          </div>
        );
      case "managers":
        return (
          <div className="zone-settings__tab-panel">
            <ZoneSettingsManagers container={formData} />
          </div>
        );
      default:
        return null;
    }
  })();

  if (!token) return null;

  return (
    <div className="settings-page-wrapper settings-page-wrapper--wizard">
      <PageHeader
        title={t("settingsContainers.createNewZone")}
        showBackButton={true}
        backRoute="/settings/containers"
        backLabel={t("settingsContainers.quit")}
        tabs={[]}
      />
      <SettingsCreateWizardLayout
        steps={steps}
        currentIndex={currentStepIndex}
        maxReachedIndex={maxReachedStepIndex}
        onStepClick={goToCreateStep}
        ariaLabel={t("settingsContainers.wizardAriaLabel")}
        stepperClassName="ui-stepper--labels-wrap"
        contentClassName={[
          "settings-create-wizard--zone",
          activeStep === "contenant"
            ? "settings-create-wizard--zone-contenant"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        panelClassName={
          activeStep === "contenant"
            ? "settings-create-wizard__panel--zone-contenant"
            : activeStep === "materials" || activeStep === "managers"
              ? "settings-create-wizard__panel--zone-step"
              : ""
        }
      >
        {stepContent}
      </SettingsCreateWizardLayout>

      <SettingsWizardFooter
        stepIndex={currentStepIndex}
        saving={saving}
        onBack={() => goToStep(steps[currentStepIndex - 1].key)}
        onQuit={async () => {
          if (!hasUnsavedChanges) {
            clearZoneCreateDraft();
            history.push("/settings/containers");
            return;
          }
          const id = await createZone();
          if (id) {
            clearZoneCreateDraft();
            history.push("/settings/containers");
          }
        }}
        quitLabel={
          hasUnsavedChanges
            ? t("settingsContainers.quitAndSave")
            : t("settingsContainers.quit")
        }
        primaryLabel={
          isLastStep ? t("settingsContainers.createTheZone") : t("Continue")
        }
        onPrimary={
          isLastStep
            ? async () => {
                const id = await createZone();
                if (id) {
                  history.push(`/settings/containers/${id}`, {
                    from: "settings",
                  });
                }
              }
            : goToNextStep
        }
        primaryDisabled={
          saving || (activeStep === "contenant" && Boolean(errorForm))
        }
      />
    </div>
  );
};

export default ZoneSettingsCreateEditor;
